import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    Chip,
    Tooltip,
    InputAdornment,
    CircularProgress,
    Alert,
    Stack,
    FormControlLabel,
    Snackbar
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    LocationOn as LocationOnIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import {
    getAllLocations,
    createLocation,
    updateLocation,
    deleteLocation
} from '../../api/locationAPI';

const LocationsPage = () => {
    // Current logged in user & admin check from Redux
    const reduxUser = useSelector((state) => state.tokens?.user);
    const reduxPermission = useSelector((state) => state.tokens?.permission || state.tokens?.user?.permission);

    const user = useMemo(() => {
        if (reduxUser && typeof reduxUser === 'object') return reduxUser;
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch (e) {
            return {};
        }
    }, [reduxUser]);

    const isAdmin = Boolean(
        reduxPermission === 'Admin' ||
        reduxPermission === 'admin' ||
        user?.permission === 'Admin' ||
        user?.role === 'Admin' ||
        user?.isAdmin ||
        user?.permission === 'admin'
    );

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'

    // Form Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isActive: true
    });
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Snackbar Notification
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Fetch locations from backend
    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllLocations();
            const list = Array.isArray(res?.data) ? res.data : [];
            setLocations(list);
        } catch (err) {
            console.error('Failed to fetch locations:', err);
            showSnackbar(err?.response?.data?.message || 'Failed to load locations', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    // Open Modal for Add
    const handleOpenAdd = () => {
        setEditingLocation(null);
        setFormData({
            name: '',
            description: '',
            isActive: true
        });
        setFormError('');
        setModalOpen(true);
    };

    // Open Modal for Edit
    const handleOpenEdit = (location) => {
        setEditingLocation(location);
        setFormData({
            name: location.name || '',
            description: location.description || '',
            isActive: location.isActive !== false
        });
        setFormError('');
        setModalOpen(true);
    };

    // Submit Add or Edit
    const handleSave = async (e) => {
        if (e) e.preventDefault();
        const trimmedName = formData.name.trim();

        if (!trimmedName) {
            setFormError('Location name is required');
            return;
        }

        setFormSubmitting(true);
        setFormError('');

        try {
            if (editingLocation) {
                // Update
                await updateLocation(editingLocation._id, {
                    name: trimmedName,
                    description: formData.description.trim(),
                    isActive: formData.isActive
                });
                showSnackbar(`Location "${trimmedName}" updated successfully`);
            } else {
                // Create
                await createLocation({
                    name: trimmedName,
                    description: formData.description.trim(),
                    isActive: formData.isActive
                });
                showSnackbar(`Location "${trimmedName}" created successfully`);
            }
            setModalOpen(false);
            fetchLocations();
        } catch (err) {
            console.error('Error saving location:', err);
            const msg = err?.response?.data?.message || 'Failed to save location';
            setFormError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setFormSubmitting(false);
        }
    };

    // Quick toggle active status
    const handleToggleStatus = async (location) => {
        try {
            const updatedStatus = !location.isActive;
            await updateLocation(location._id, {
                isActive: updatedStatus
            });
            setLocations(prev =>
                prev.map(l => (l._id === location._id ? { ...l, isActive: updatedStatus } : l))
            );
            showSnackbar(`Location "${location.name}" marked as ${updatedStatus ? 'Active' : 'Inactive'}`);
        } catch (err) {
            console.error('Failed to toggle status:', err);
            showSnackbar(err?.response?.data?.message || 'Failed to update status', 'error');
        }
    };

    // Confirm Delete
    const handleConfirmDelete = async () => {
        if (!locationToDelete) return;
        setDeleting(true);
        try {
            await deleteLocation(locationToDelete._id);
            showSnackbar(`Location "${locationToDelete.name}" deleted successfully`);
            setDeleteModalOpen(false);
            setLocationToDelete(null);
            fetchLocations();
        } catch (err) {
            console.error('Failed to delete location:', err);
            showSnackbar(err?.response?.data?.message || 'Failed to delete location', 'error');
        } finally {
            setDeleting(false);
        }
    };

    // Filtered locations
    const filteredLocations = useMemo(() => {
        return locations.filter(loc => {
            const matchesQuery = loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (loc.description && loc.description.toLowerCase().includes(searchQuery.toLowerCase()));

            if (!matchesQuery) return false;

            if (statusFilter === 'ACTIVE') return loc.isActive !== false;
            if (statusFilter === 'INACTIVE') return loc.isActive === false;
            return true;
        });
    }, [locations, searchQuery, statusFilter]);

    // Access control: Admin only
    if (!isAdmin) {
        return (
            <Box sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #fee2e2', bgcolor: '#fff5f5' }}>
                    <AdminIcon sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#991b1b', mb: 1 }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#7f1d1d', mb: 3 }}>
                        Only users with Administrator permissions can view and manage Location Masters.
                    </Typography>
                    <Chip label={`Current Role: ${user?.permission || user?.role || 'Guest'}`} color="error" variant="outlined" />
                </Paper>
            </Box>
        );
    }

    const activeCount = locations.filter(l => l.isActive !== false).length;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2.5,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#fff'
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: 'rgba(59, 130, 246, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#60a5fa'
                            }}
                        >
                            <LocationOnIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                                    Location Master
                                </Typography>
                                <Chip
                                    label="Admin Only"
                                    size="small"
                                    sx={{ bgcolor: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', fontWeight: 600, border: '1px solid rgba(239, 68, 68, 0.4)' }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                                Manage approved destinations & locations required for inquiries and packages.
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip
                            label={`${locations.length} Total`}
                            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 600 }}
                        />
                        <Chip
                            label={`${activeCount} Active`}
                            sx={{ bgcolor: 'rgba(16, 185, 129, 0.2)', color: '#6ee7b7', fontWeight: 600 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAdd}
                            sx={{
                                bgcolor: '#3b82f6',
                                '&:hover': { bgcolor: '#2563eb' },
                                fontWeight: 600,
                                px: 2.5,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)'
                            }}
                        >
                            Add Location
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {/* Filter and Search Bar */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search locations by name or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ minWidth: { xs: '100%', sm: 350 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 2 }
                        }}
                    />

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            size="small"
                            variant={statusFilter === 'ALL' ? 'contained' : 'outlined'}
                            onClick={() => setStatusFilter('ALL')}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                            All ({locations.length})
                        </Button>
                        <Button
                            size="small"
                            variant={statusFilter === 'ACTIVE' ? 'contained' : 'outlined'}
                            color="success"
                            onClick={() => setStatusFilter('ACTIVE')}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                            Active ({activeCount})
                        </Button>
                        <Button
                            size="small"
                            variant={statusFilter === 'INACTIVE' ? 'contained' : 'outlined'}
                            color="warning"
                            onClick={() => setStatusFilter('INACTIVE')}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                            Inactive ({locations.length - activeCount})
                        </Button>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchLocations} size="small" sx={{ ml: 1 }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </Paper>

            {/* Locations Table */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 14px rgba(0,0,0,0.05)' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: 60, color: '#475569' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Location Name</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 140, color: '#475569' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 140, color: '#475569' }}>Created Date</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, width: 120, color: '#475569' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={36} />
                                        <Typography variant="body2" sx={{ mt: 1.5, color: '#64748b' }}>
                                            Loading locations...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredLocations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <LocationOnIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#64748b' }}>
                                            No locations found
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                                            {searchQuery ? 'Try adjusting your search criteria' : 'Click "Add Location" to insert your first location'}
                                        </Typography>
                                        {!searchQuery && (
                                            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                                                Add Location
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredLocations.map((loc, idx) => (
                                    <TableRow
                                        key={loc._id}
                                        hover
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            bgcolor: loc.isActive === false ? '#fafafa' : 'inherit'
                                        }}
                                    >
                                        <TableCell sx={{ color: '#94a3b8', fontWeight: 500 }}>
                                            {idx + 1}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: 1.5,
                                                        bgcolor: loc.isActive !== false ? '#eff6ff' : '#f1f5f9',
                                                        color: loc.isActive !== false ? '#3b82f6' : '#94a3b8',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <LocationOnIcon fontSize="small" />
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: loc.isActive !== false ? '#1e293b' : '#64748b'
                                                        }}
                                                    >
                                                        {loc.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748b' }}>
                                            {loc.description || '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Switch
                                                    size="small"
                                                    checked={loc.isActive !== false}
                                                    onChange={() => handleToggleStatus(loc)}
                                                    color="success"
                                                />
                                                <Chip
                                                    label={loc.isActive !== false ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={loc.isActive !== false ? 'success' : 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748b', fontSize: 13 }}>
                                            {loc.createdAt ? new Date(loc.createdAt).toLocaleDateString('en-IN', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            }) : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Edit Location">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEdit(loc)}
                                                        sx={{ color: '#2563eb', '&:hover': { bgcolor: '#eff6ff' } }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Location">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setLocationToDelete(loc);
                                                            setDeleteModalOpen(true);
                                                        }}
                                                        sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Add / Edit Dialog */}
            <Dialog
                open={modalOpen}
                onClose={() => !formSubmitting && setModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2.5 } }}
            >
                <form onSubmit={handleSave}>
                    <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                        {editingLocation ? 'Edit Location' : 'Add New Location'}
                    </DialogTitle>
                    <DialogContent dividers sx={{ pt: 2 }}>
                        {formError && (
                            <Alert severity="error" sx={{ mb: 2.5 }}>
                                {formError}
                            </Alert>
                        )}
                        <Stack spacing={2.5}>
                            <TextField
                                autoFocus
                                label="Location Name"
                                placeholder="e.g. North Sikkim, Darjeeling, Kerala"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                helperText="Must be unique. Inquiries and packages will use this exact approved name."
                            />

                            <TextField
                                label="Description (Optional)"
                                placeholder="Brief overview or key highlights for this location..."
                                fullWidth
                                multiline
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                        color="success"
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Active for Selection in Inquiries & Packages
                                    </Typography>
                                }
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button
                            onClick={() => setModalOpen(false)}
                            disabled={formSubmitting}
                            sx={{ color: '#64748b' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={formSubmitting}
                            sx={{
                                bgcolor: '#3b82f6',
                                '&:hover': { bgcolor: '#2563eb' },
                                fontWeight: 600,
                                px: 3
                            }}
                        >
                            {formSubmitting ? (
                                <CircularProgress size={20} sx={{ color: '#fff' }} />
                            ) : editingLocation ? 'Update Location' : 'Create Location'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteModalOpen}
                onClose={() => !deleting && setDeleteModalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2.5 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#ef4444' }}>
                    Delete Location?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete the location <strong>"{locationToDelete?.name}"</strong>?
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                        This action cannot be undone. Inquiries or packages that already reference this location will keep their historical record.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setDeleteModalOpen(false)}
                        disabled={deleting}
                        sx={{ color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        sx={{ fontWeight: 600, px: 2.5 }}
                    >
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 3 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LocationsPage;
